<template>
  <div>
    <EmptyState
      v-if="!loading && totalRecords === 0"
      class="pt-8 pb-12 px-6"
      :icon="'mdi-beehive-outline'"
      :iconSize="36"
      :title="$t('routines.executions_empty')"
    />
    <v-data-table
      v-else
      data-private
      :footer-props="footerOptions"
      :headers="headers"
      :items="rows"
      :mobile-breakpoint="$vuetify.breakpoint.thresholds.md"
      :options.sync="tableOptions"
      :server-items-length="totalRecords"
      :loading="loading"
      :no-data-text="$t('routines.definitions_empty_state')"
      @click:row="goToRoutine($event)"
    >
      <template v-slot:item.name="{ item }">
        <v-tooltip top max-width="300px">
          <template v-slot:activator="{ on }">
            <router-link class="router-link" :to="goToRoutine(item)">
              <span v-on="on" v-snip="1" class="body-2 primary--text">{{
                item.name
              }}</span>
            </router-link>
          </template>
          {{ item.name }}
        </v-tooltip>
      </template>

      <template v-slot:item.status="{ item }">
        <Status :entity="item" />
      </template>

      <template v-slot:item.createdAt="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">{{
          item.createdAt
        }}</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import api from '@/api'
import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'
import _ from 'lodash'

import Status from '@/components/Core/StatusChip.vue'
import EmptyState from '@/components/EmptyState.vue'

const INITIAL_ITEMS_PER_PAGE = 20
const DEFAULT_SORT = 'createdAt'

const sortOrder = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
}

export default {
  name: 'RoutineSection',

  props: {
    organizationId: {
      required: true,
      type: String,
    },
    user: {
      required: true,
      type: Object,
    },
  },

  mixins: [mixin],

  components: {
    Status,
    EmptyState,
  },

  mounted() {
    this.fetchRoutines(this.tableOptions)
  },

  data() {
    return {
      routines: [],
      loading: false,
      totalRecords: 0,
      lastRequestKey: null,
      requestSequence: 0,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('routines.routine_execution'),
          value: 'name',
          sortable: true,
          width: 520,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
          sortable: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('home.teams_activity.execution_date'),
          value: 'createdAt',
          width: 260,
          sortable: true,
          cellClass: 'cursor-pointer',
        },
      ],
    }
  },

  methods: {
    async fetchRoutines(options) {
      if (_.isEmpty(this.user)) return
      const size = options?.itemsPerPage ?? INITIAL_ITEMS_PER_PAGE
      const page = options?.page ?? 1
      const requestParams = {
        filters: {
          createdBy: this.accountId,
        },
        sort: {
          by: options?.sortBy ?? DEFAULT_SORT,
          order: options?.sortDesc === false
            ? sortOrder.ASCENDING
            : sortOrder.DESCENDING,
        },
        limit: size,
        skip: size * (page - 1),
      }

      const requestKey = JSON.stringify(requestParams)
      if (requestKey === this.lastRequestKey) return
      this.lastRequestKey = requestKey
      this.requestSequence += 1
      const requestId = this.requestSequence

      try {
        this.loading = true
        const { data } = await api.routine.getRoutineExecutions(
          this.organizationId,
          requestParams
        )

        if (requestId !== this.requestSequence) return

        this.routines = data.results ?? []
        this.totalRecords = data.totalRecordsInData ?? 0
      } catch (err) {
        if (requestId !== this.requestSequence) return
        this.lastRequestKey = null
        this.handleRequestError(err)
      } finally {
        if (requestId === this.requestSequence) this.loading = false
      }
    },

    goToRoutine(routine) {
      return `/routine-executions/${routine.id}`
    },
  },

  computed: {
    accountId() {
      return this.user?.id ?? ''
    },

    rows() {
      return this.routines.map(routine => {
        return {
          id: routine.id,
          name: routine.name,
          status: routine.status,
          createdAt: routine.createdAt
            ? utils.dates.formatDate(routine.createdAt, 'MMM D, YYYY, h:mm A')
            : '',
        }
      })
    },

    tableOptions: {
      get() {
        return {
          itemsLength: this.totalRecords,
          itemsPerPage: INITIAL_ITEMS_PER_PAGE,
          page: 1,
          multiSort: false,
          mustSort: true,
        }
      },

      set(event) {
        this.fetchRoutines({
          itemsLength: this.totalRecords,
          itemsPerPage: event.itemsPerPage,
          page: event.page,
          sortBy: event.sortBy[0] ?? DEFAULT_SORT,
          sortDesc: _.isEmpty(event.sortBy) ? true : event.sortDesc[0],
        })
      },
    },
  },
  watch: {
    user: {
      deep: true,
      handler(val) {
        if (!val) return
        this.fetchRoutines(this.tableOptions)
      },
    },
  },
}
</script>
<style lang="scss" scoped>
.v-application a.router-link {
  color: $color-blue-grey-darken3;
  text-decoration: none;
}
</style>
